import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography,
  Button,
  Box,
  Container,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Pagination,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  OutlinedInput,
  Tabs,
  Tab,
  MenuItem,
  Stack,
  Tooltip,
  useMediaQuery,
  useTheme,
  IconButton,
} from '@mui/material';
import {
  Refresh,
  Add,
  Delete,
  Restore,
  ContentCopy,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getStoredToken,
  getApiKeys,
  createApiKey,
  activateApiKey,
  deactivateApiKey,
  getCompanyPickerConfig,
  getKivoBranches,
  refreshKivoCompanyBranches,
} from '../utils/api';
import PageHeader from './PageHeader';
import { formatDate } from '../utils/dateUtils';

const initialCreateDialogState = {
  open: false,
  name: '',
  companyId: '',
  branchId: '',
  deviceName: '',
  baseUrl: '',
};

const ApiKeys = () => {
  const navigate = useNavigate();
  const { permissions } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [createDialog, setCreateDialog] = useState(initialCreateDialogState);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [activeTab, setActiveTab] = useState(0); // 0 for active, 1 for inactive
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 10,
    offset: 0,
    has_next: false,
    has_prev: false,
    total_pages: 0,
    current_page: 1,
  });
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyNameMap, setCompanyNameMap] = useState({});
  const [branchOptions, setBranchOptions] = useState({});
  const [branchNameMap, setBranchNameMap] = useState({});
  const [createBranchesRefreshing, setCreateBranchesRefreshing] = useState(false);
  const branchOptionsRef = useRef({});
  const branchesInFlightRef = useRef(new Set());
  const [companyMenuWidth, setCompanyMenuWidth] = useState(null);
  const [branchMenuWidth, setBranchMenuWidth] = useState(null);

  useEffect(() => {
    branchOptionsRef.current = branchOptions;
  }, [branchOptions]);

  const fetchBranchesForCompany = useCallback(async (companyId, forceReload = false) => {
    const cachedBranchOptions = branchOptionsRef.current;
    if (!companyId || (cachedBranchOptions[companyId] && !forceReload)) {
      return cachedBranchOptions[companyId] || [];
    }
    if (branchesInFlightRef.current.has(companyId)) {
      return cachedBranchOptions[companyId] || [];
    }
    branchesInFlightRef.current.add(companyId);
    try {
      const data = await getKivoBranches(companyId);
      const branchList = Array.isArray(data?.branches) ? data.branches : [];
      setBranchOptions((prev) => ({ ...prev, [companyId]: branchList }));
      if (branchList.length) {
        setBranchNameMap((prev) => {
          const next = { ...prev };
          branchList.forEach((branch) => {
            if (branch.branch_id) {
              next[branch.branch_id] = branch.branch_name || branch.branch_address || branch.address || 'Unnamed branch';
            }
          });
          return next;
        });
      }
      return branchList;
    } catch (error) {
      console.error(`Failed to fetch branches for company ${companyId}:`, error);
      throw error;
    } finally {
      branchesInFlightRef.current.delete(companyId);
    }
  }, []);

  const loadApiKeys = useCallback(async (limit = 10, offset = 0, tabValue = activeTab) => {
    setLoading(true);
    try {
      // Ensure limit and offset are integers
      const limitInt = parseInt(limit) || 10;
      const offsetInt = parseInt(offset) || 0;
      console.log('Loading API keys with limit:', limitInt, 'offset:', offsetInt);
      
      const data = await getApiKeys(limitInt, offsetInt);
      console.log('API Keys response:', data);

      if (Array.isArray(data)) {
        // Filter keys based on active tab
        const filteredKeys = data.filter(key => 
          tabValue === 0 ? key.is_active : !key.is_active
        );
        setApiKeys(filteredKeys);
        const companyIds = [...new Set(filteredKeys.map((k) => k.company_id).filter(Boolean))];
        companyIds.forEach((id) => fetchBranchesForCompany(id));
        // If no pagination info, create basic pagination
        setPagination({
          total: filteredKeys.length,
          limit: limitInt,
          offset: offsetInt,
          has_next: filteredKeys.length === limitInt,
          has_prev: offsetInt > 0,
          total_pages: Math.ceil(filteredKeys.length / limitInt),
          current_page: Math.floor(offsetInt / limitInt) + 1,
        });
      } else if (data.api_keys && data.pagination) {
        // Filter keys based on active tab
        const filteredKeys = data.api_keys.filter(key => 
          tabValue === 0 ? key.is_active : !key.is_active
        );
        setApiKeys(filteredKeys);
        const companyIds = [...new Set(filteredKeys.map((k) => k.company_id).filter(Boolean))];
        companyIds.forEach((id) => fetchBranchesForCompany(id));
        setPagination({
          ...data.pagination,
          total: filteredKeys.length,
        });
      } else {
        setApiKeys([]);
      }
    } catch (error) {
      console.error('Error loading API keys:', error);
      showSnackbar('Failed to load API keys', 'error');
    } finally {
      setLoading(false);
    }
  }, [activeTab, fetchBranchesForCompany]);

  const loadCompanyOptions = useCallback(async () => {
    setCompanyLoading(true);
    try {
      const data = await getCompanyPickerConfig(permissions);
      const configs = Array.isArray(data?.configs) ? data.configs : [];
      const seen = new Set();
      const options = [];
      configs.forEach((cfg) => {
        const companyId = cfg.company_id;
        const companyName = cfg.company_name || 'Default Company';
        if (!companyId || seen.has(companyId)) return;
        seen.add(companyId);
        options.push({ companyId, companyName });
      });
      setCompanyOptions(options);
      const nameMap = {};
      options.forEach(({ companyId, companyName }) => {
        nameMap[companyId] = companyName;
      });
      setCompanyNameMap(nameMap);
    } catch (error) {
      console.error('Failed to load company options:', error);
    } finally {
      setCompanyLoading(false);
    }
  }, [permissions]);

  const handleCreateFetchBranches = async (companyId) => {
    if (!companyId || createBranchesRefreshing) return;
    setCreateBranchesRefreshing(true);
    try {
      await refreshKivoCompanyBranches(companyId);
      const branches = await fetchBranchesForCompany(companyId, true);
      if (branches.length > 0) {
        showSnackbar('Branches fetched successfully', 'success');
        setCreateDialog((prev) => ({
          ...prev,
          branchId: '',
        }));
      } else {
        showSnackbar(
          'Kivo did not return any branches for this company. Check the company in Kivo.',
          'warning',
        );
      }
    } catch (error) {
      showSnackbar(error?.message || 'Failed to fetch branches', 'error');
    } finally {
      setCreateBranchesRefreshing(false);
    }
  };

  const handleCompanySelection = async (companyId) => {
    setCreateDialog((prev) => ({
      ...prev,
      companyId,
      branchId: '',
    }));
    if (companyId) {
      try {
        const branches = await fetchBranchesForCompany(companyId);
        if (branches.length === 0) {
          showSnackbar('No branches found for this company. Fetch branches', 'warning');
        }
      } catch (error) {
        showSnackbar(error?.message || 'Failed to load branches', 'error');
      }
    }
  };

  const handleBranchSelection = (branchId) => {
    setCreateDialog((prev) => ({
      ...prev,
      branchId,
      // Device name is resolved server-side via DEVICE_NAME env (if not provided).
      deviceName: prev.deviceName,
    }));
  };

  const resetCreateDialog = () => setCreateDialog({ ...initialCreateDialogState });

  useEffect(() => {
    const token = getStoredToken();
    if (!token) {
      navigate('/login');
      return;
    }
    loadApiKeys(10, 0);
  }, [navigate, loadApiKeys]);

  useEffect(() => {
    loadCompanyOptions();
  }, [loadCompanyOptions]);

  const currentBranchOptions = createDialog.companyId
    ? branchOptions[createDialog.companyId] || []
    : [];
  const createBranchFieldLabelNotched =
    Boolean(createDialog.branchId) ||
    createBranchesRefreshing ||
    (Boolean(createDialog.companyId) && currentBranchOptions.length === 0);

  const getCompanyLabel = (companyId) =>
    companyId ? companyNameMap[companyId] || companyId : 'Not linked';

  const getBranchLabel = (branchId) =>
    branchId ? branchNameMap[branchId] || branchId : 'Not linked';

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCopyApiKey = (apiKey) => {
    navigator.clipboard.writeText(apiKey).then(() => {
      showSnackbar('API key copied to clipboard', 'success');
    }).catch((error) => {
      console.error('Failed to copy API key:', error);
      showSnackbar('Failed to copy API key', 'error');
    });
  };

  const handleCreateApiKey = () => {
    const defaultCompanyId = companyOptions?.[0]?.companyId || '';
    setCreateDialog({
      ...initialCreateDialogState,
      open: true,
      companyId: defaultCompanyId,
    });
    if (defaultCompanyId) {
      fetchBranchesForCompany(defaultCompanyId);
    }
  };

  const handleCreateSubmit = async () => {
    const trimmedName = createDialog.name.trim();
    if (!trimmedName) {
      showSnackbar('Please enter a name for the API key', 'error');
      return;
    }
    if (!createDialog.companyId || !createDialog.branchId) {
      showSnackbar('Company and branch are required', 'error');
      return;
    }
    if (!createDialog.baseUrl || !createDialog.baseUrl.trim()) {
      showSnackbar('Base URL is required', 'error');
      return;
    }

    try {
      const response = await createApiKey({
        keyName: trimmedName,
        expiresDays: null,
        companyId: createDialog.companyId,
        branchId: createDialog.branchId,
        // Device name is provided by server env DEVICE_NAME (or backend will reject if not set).
        deviceName: (createDialog.deviceName || null),
        baseUrl: createDialog.baseUrl.trim(),
      });
      
      if (response) {
        showSnackbar('API key created successfully', 'success');
        resetCreateDialog();
        loadApiKeys(pagination.limit, pagination.offset);
      }
    } catch (error) {
      console.error('Error creating API key:', error);
      // Display the actual error message from the backend
      const errorMessage = error.message || 'Failed to create API key';
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleDeleteApiKey = (apiKey) => {
    setConfirmDialog({
      open: true,
      title: 'Deactivate API Key',
      message: `Are you sure you want to deactivate the API key "${apiKey.key_name}"? This will make it inactive and it won't be able to access the API.`,
      onConfirm: () => performDeactivate(apiKey.key_id),
    });
  };

  const performDeactivate = async (keyId) => {
    try {
      const response = await deactivateApiKey(keyId);
      if (response) {
        showSnackbar('API key deactivated successfully', 'success');
        loadApiKeys(pagination.limit, pagination.offset);
      } else {
        showSnackbar('Failed to deactivate API key', 'error');
      }
    } catch (error) {
      console.error('Error deactivating API key:', error);
      showSnackbar('Failed to deactivate API key', 'error');
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPagination(prev => ({ ...prev, offset: 0, current_page: 1 })); // Reset pagination
    // Load the correct tab's data immediately so "Updated By" reflects after deactivate/restore.
    loadApiKeys(pagination.limit, 0, newValue);
  };

  const handleReactivateApiKey = (apiKey) => {
    setConfirmDialog({
      open: true,
      title: 'Reactivate API Key',
      message: `Are you sure you want to reactivate the API key "${apiKey.key_name}"? This will make it active and it will be able to access the API again.`,
      onConfirm: () => performReactivate(apiKey.key_id),
    });
  };

  const performReactivate = async (keyId) => {
    try {
      const response = await activateApiKey(keyId);
      if (response) {
        showSnackbar('API key reactivated successfully', 'success');
        loadApiKeys(pagination.limit, pagination.offset);
      } else {
        showSnackbar('Failed to reactivate API key', 'error');
      }
    } catch (error) {
      console.error('Error reactivating API key:', error);
      // Display the actual error message from the backend
      const errorMessage = error.message || 'Failed to reactivate API key';
      showSnackbar(errorMessage, 'error');
    }
  };


  const handlePageChange = (event, page) => {
    const newOffset = (page - 1) * pagination.limit;
    loadApiKeys(pagination.limit, newOffset);
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value);
    loadApiKeys(newLimit, 0);
  };

  const renderUserTimestampCell = (nameValue, timestampValue) => {
    const name = nameValue || '-';
    return (
      <Stack spacing={0.25}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatDate(timestampValue)}
        </Typography>
      </Stack>
    );
  };

  const maskApiKey = (apiKeyValue) => {
    if (!apiKeyValue || typeof apiKeyValue !== 'string') return '–';
    if (apiKeyValue.length <= 16) return apiKeyValue;
    return `${apiKeyValue.slice(0, 8)}***${apiKeyValue.slice(-8)}`;
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Container maxWidth="lg">
        <PageHeader 
          title="API Key Management" 
          subtitle="Manage API keys for accessing the Employee Management system. Keep your keys secure and rotate them regularly."
        />

        <Card variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden', mb: 2 }}>
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              API Keys
            </Typography>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ width: { xs: '100%', sm: 'auto' }, px: { xs: 1, sm: 0 } }}
            >
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => loadApiKeys(pagination.limit, pagination.offset)}
                disabled={loading}
                fullWidth={isMobile}
              >
                Refresh
              </Button>
              {activeTab === 0 && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleCreateApiKey}
                  fullWidth={isMobile}
                >
                  Create API Key
                </Button>
              )}
            </Stack>
          </Box>

          <Box sx={{ px: { xs: 2, sm: 3 }, pt: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label="API keys tabs"
              variant="scrollable"
              allowScrollButtonsMobile
            >
              <Tab label="Active Keys" />
              <Tab label="Inactive Keys" />
            </Tabs>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 0 } }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : apiKeys.length === 0 ? (
              <Box sx={{ p: 2 }}>
                <Typography variant="body2" color="text.secondary" align="center">
                  {activeTab === 0
                    ? 'No active API keys found. Create your first API key to get started.'
                    : 'No inactive API keys found.'}
                </Typography>
              </Box>
            ) : isMobile ? (
              <Stack spacing={1.5}>
                {apiKeys.map((apiKey) => (
                  <Card key={apiKey.key_id} variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1 }}>
                          {apiKey.key_name || 'Unnamed key'}
                        </Typography>
                        <Chip
                          size="small"
                          label={apiKey.is_active ? 'Active' : 'Inactive'}
                          color={apiKey.is_active ? 'success' : 'primary'}
                          sx={{ borderRadius: '8px', fontWeight: 600 }}
                        />
                      </Stack>

                      <Stack spacing={0.75}>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Company:</strong> {getCompanyLabel(apiKey.company_id)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Branch:</strong> {getBranchLabel(apiKey.branch_id)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Device:</strong> {apiKey.device_name || '–'}
                        </Typography>
	                        <Box>
	                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
	                            <Typography
	                              variant="body2"
	                              color="text.secondary"
	                              sx={{ fontFamily: 'monospace', wordBreak: 'break-all', flex: 1 }}
	                            >
	                              <strong>API Key:</strong> {maskApiKey(apiKey.api_key)}
	                            </Typography>
	                            <IconButton
	                              size="small"
	                              onClick={() => handleCopyApiKey(apiKey.api_key)}
	                              disabled={!apiKey.api_key}
	                              aria-label="Copy API key"
	                              title="Copy API Key"
	                            >
	                              <ContentCopy fontSize="small" />
	                            </IconButton>
	                          </Box>
	                        </Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
                        >
                          <strong>Base URL:</strong> {apiKey.base_url || '–'}
                        </Typography>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Created By:</strong> {apiKey.created_by_name || apiKey.created_by || '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(apiKey.created_at)}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Updated By:</strong> {apiKey.updated_by_name || apiKey.updated_by || '-'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(apiKey.updated_at)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          <strong>Last Used:</strong> {formatDate(apiKey.last_used)}
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                        {activeTab === 0 ? (
                          <Button
                            title="Deactivate API Key"
                            size="small"
                            variant="outlined"
                            color="warning"
                            startIcon={<Delete />}
                            onClick={() => handleDeleteApiKey(apiKey)}
                            fullWidth
                          >
                            Deactivate
                          </Button>
                        ) : (
                          <Button
                            title="Reactivate API Key"
                            size="small"
                            variant="outlined"
                            color="success"
                            startIcon={<Restore />}
                            onClick={() => handleReactivateApiKey(apiKey)}
                            fullWidth
                          >
                            Reactivate
                          </Button>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
	            ) : (
	              <Box sx={{ overflowX: 'auto' }}>
	                <Table sx={{ minWidth: 1200 }}>
	                  <TableHead sx={{ bgcolor: 'grey.50' }}>
	                    <TableRow>
	                      <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
	                      <TableCell sx={{ fontWeight: 600 }}>Company</TableCell>
	                      <TableCell sx={{ fontWeight: 600 }}>Branch</TableCell>
	                      <TableCell sx={{ fontWeight: 600, minWidth: 240 }}>API Key</TableCell>
	                      <TableCell sx={{ fontWeight: 600 }}>Device</TableCell>
	                      <TableCell sx={{ fontWeight: 600, minWidth: 240 }}>Base URL</TableCell>
	                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
	                      <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
	                      <TableCell sx={{ fontWeight: 600 }}>Updated By</TableCell>
	                      <TableCell sx={{ fontWeight: 600 }}>Last Used</TableCell>
	                      <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {apiKeys.map((apiKey) => {
                      const statusStyles = apiKey.is_active
                        ? { bgcolor: '#dcfce7', color: '#166534' }
                        : { bgcolor: '#e5e7eb', color: '#374151' };

                      return (
                        <TableRow key={apiKey.key_id} hover>
                          <TableCell>
                            <Typography sx={{ fontWeight: 600 }}>
                              {apiKey.key_name || 'Unnamed key'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>
                            {getCompanyLabel(apiKey.company_id)}
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>
                            {getBranchLabel(apiKey.branch_id)}
                          </TableCell>
		                          <TableCell sx={{ minWidth: 240 }}>
		                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
		                              <Tooltip title={maskApiKey(apiKey.api_key)} placement="top" arrow>
		                                <Typography
		                                  variant="body2"
		                                  noWrap
		                                  sx={{
		                                    fontFamily: 'monospace',
		                                    fontSize: '0.85rem',
		                                    minWidth: 0,
		                                    overflow: 'hidden',
		                                    textOverflow: 'ellipsis',
		                                    flex: 1,
		                                  }}
		                                >
		                                  {maskApiKey(apiKey.api_key)}
		                                </Typography>
		                              </Tooltip>
		                              <Tooltip title="Copy API Key" placement="top" arrow>
		                                <span>
		                                  <IconButton
		                                    size="small"
		                                    onClick={() => handleCopyApiKey(apiKey.api_key)}
		                                    disabled={!apiKey.api_key}
		                                    aria-label="Copy API key"
		                                  >
		                                    <ContentCopy fontSize="small" />
		                                  </IconButton>
		                                </span>
		                              </Tooltip>
		                            </Box>
		                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>
                            {apiKey.device_name || '–'}
                          </TableCell>
	                          <TableCell sx={{ minWidth: 240 }}>
	                            <Tooltip title={apiKey.base_url || ''} placement="top" arrow disableHoverListener={!apiKey.base_url}>
	                              <Typography
	                                variant="body2"
	                                noWrap
	                                sx={{
	                                  fontFamily: 'monospace',
	                                  fontSize: '0.85rem',
	                                  maxWidth: 420,
	                                  overflow: 'hidden',
	                                  textOverflow: 'ellipsis',
	                                  color: apiKey.base_url ? 'text.primary' : 'text.secondary',
	                                }}
	                              >
	                                {apiKey.base_url || '–'}
	                              </Typography>
	                            </Tooltip>
	                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={apiKey.is_active ? 'Active' : 'Inactive'}
                              sx={{ ...statusStyles, fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 180 }}>
                            {renderUserTimestampCell(
                              apiKey.created_by_name || apiKey.created_by,
                              apiKey.created_at
                            )}
                          </TableCell>
                          <TableCell sx={{ minWidth: 180 }}>
                            {renderUserTimestampCell(
                              apiKey.updated_by_name || apiKey.updated_by,
                              apiKey.updated_at
                            )}
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary' }}>
                            {formatDate(apiKey.last_used)}
                          </TableCell>
	                          <TableCell align="right">
	                            <Box
	                              sx={{
	                                display: 'inline-flex',
	                                flexDirection: 'row',
	                                gap: 1,
	                                px: 1.5,
	                                py: 1.25,
	                                borderRadius: 2,
	                                border: '1px solid',
	                                borderColor: 'divider',
	                                bgcolor: 'grey.50',
	                              }}
	                            >
	                              {activeTab === 0 ? (
	                                <Button
	                                  size="small"
	                                  variant="outlined"
                                  color="warning"
                                  startIcon={<Delete />}
                                  onClick={() => handleDeleteApiKey(apiKey)}
                                >
                                  Deactivate
                                </Button>
                              ) : (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                  startIcon={<Restore />}
                                  onClick={() => handleReactivateApiKey(apiKey)}
                                >
                                  Reactivate
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Box>
        </Card>

        <Box>
          {/* Pagination Controls */}
          {!loading && pagination.total > 0 && (
              <Box sx={{ mt: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  justifyContent="space-between"
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                  spacing={2}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'space-between', sm: 'flex-start' } }}>
                    <Typography variant="body2" color="text.secondary">
                      Items per page:
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 80 }}>
                      <Select
                        value={pagination.limit}
                        onChange={handleLimitChange}
                        displayEmpty
                      >
                        <MenuItem value={5}>5</MenuItem>
                        <MenuItem value={10}>10</MenuItem>
                        <MenuItem value={25}>25</MenuItem>
                        <MenuItem value={50}>50</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: { xs: 'left', sm: 'center' } }}>
                    Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} API keys
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: { xs: 'center', sm: 'flex-end' } }}>
                    <Pagination
                      count={pagination.total_pages}
                      page={pagination.current_page}
                      onChange={handlePageChange}
                      color="primary"
                      showFirstButton={!isMobile}
                      showLastButton={!isMobile}
                      size={isMobile ? 'small' : 'medium'}
                      siblingCount={isMobile ? 0 : 1}
                    />
                  </Box>
                </Stack>
              </Box>
            )}
        </Box>
      </Container>

      {/* Create API Key Dialog */}
      <Dialog
        open={createDialog.open}
        onClose={resetCreateDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Create New API Key</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Key Name"
            value={createDialog.name}
            onChange={(e) => setCreateDialog({ ...createDialog, name: e.target.value })}
            margin="normal"
            required
            placeholder="e.g., Mobile App Key"
          />
          <FormControl fullWidth margin="normal" disabled={companyLoading}>
            <InputLabel id="api-key-company-label">Company</InputLabel>
            <Select
              id="api-key-company-select"
              onOpen={(e) => {
                const el = document.getElementById('api-key-company-select');
                const w = el?.getBoundingClientRect?.().width;
                setCompanyMenuWidth(typeof w === 'number' && w > 0 ? w : null);
              }}
              MenuProps={{
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    minWidth: companyMenuWidth ?? undefined,
                    width: companyMenuWidth ?? undefined,
                    maxWidth: companyMenuWidth ?? undefined,
                    overflowX: 'hidden',
                  },
                },
              }}
              labelId="api-key-company-label"
              label="Company"
              value={createDialog.companyId}
              onChange={(event) => handleCompanySelection(event.target.value)}
            >
            {companyOptions.map((company) => (
              <MenuItem key={company.companyId} value={company.companyId}>
                <Typography variant="body2" noWrap sx={{ maxWidth: '100%' }}>
                  {company.companyName}
                </Typography>
              </MenuItem>
            ))}
            </Select>
            {companyLoading && (
              <Typography variant="caption" color="text.secondary">
                Loading companies…
              </Typography>
            )}
            {!companyLoading && companyOptions.length === 0 && (
              <Typography variant="caption" color="text.secondary">
                No companies configured yet.
              </Typography>
            )}
          </FormControl>
          <FormControl
            fullWidth
            margin="normal"
            disabled={!createDialog.companyId || createBranchesRefreshing}
          >
            <InputLabel
              id="api-key-branch-label"
              shrink={createBranchFieldLabelNotched}
            >
              Branch
            </InputLabel>
            <Select
              id="api-key-branch-select"
              onOpen={(e) => {
                const el = document.getElementById('api-key-branch-select');
                const w = el?.getBoundingClientRect?.().width;
                setBranchMenuWidth(typeof w === 'number' && w > 0 ? w : null);
              }}
              displayEmpty
              label="Branch"
              notched={createBranchFieldLabelNotched}
              input={(
                <OutlinedInput
                  label="Branch"
                  notched={createBranchFieldLabelNotched}
                  endAdornment={
                    createDialog.companyId && currentBranchOptions.length === 0 ? (
                      <InputAdornment position="end" sx={{ mr: 2 }}>
                        <Tooltip title="Fetch branches" placement="top" arrow>
                          <span>
                            <IconButton
                              size="small"
                              disabled={createBranchesRefreshing}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleCreateFetchBranches(createDialog.companyId);
                              }}
                              sx={{ p: 0.75 }}
                            >
                              {createBranchesRefreshing ? (
                                <CircularProgress size={16} />
                              ) : (
                                <Refresh fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </InputAdornment>
                    ) : null
                  }
                />
              )}
              MenuProps={{
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                transformOrigin: { vertical: 'top', horizontal: 'left' },
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                    minWidth: branchMenuWidth ?? undefined,
                    width: branchMenuWidth ?? undefined,
                    maxWidth: branchMenuWidth ?? undefined,
                    overflowX: 'hidden',
                  },
                },
              }}
              labelId="api-key-branch-label"
              value={createDialog.branchId}
              onChange={(event) => handleBranchSelection(event.target.value)}
              renderValue={(selected) => {
                if (createBranchesRefreshing) return 'Fetching braches…';
                if (!selected && createDialog.companyId && currentBranchOptions.length === 0) {
                  return 'No branches';
                }
                if (!selected) return '';
                const branch = currentBranchOptions.find((item) => item.branch_id === selected);
                const label = branch?.branch_address || branch?.branch_name || branch?.branch_id || '';
                if (!label) return '';
                return (
                  <Tooltip title={label} placement="top" arrow>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        maxWidth: { xs: 160, sm: 420 },
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {label}
                    </Typography>
                  </Tooltip>
                );
              }}
            >
              {currentBranchOptions.map((branch) => (
                <MenuItem key={branch.branch_id} value={branch.branch_id} sx={{ width: '100%' }}>
                  <Tooltip
                    title={branch.branch_address || branch.branch_name || ''}
                    placement="right"
                    arrow
                  >
                    <Typography variant="body2" noWrap sx={{ maxWidth: '100%' }}>
                      {branch.branch_address || branch.branch_name || branch.branch_id}
                    </Typography>
                  </Tooltip>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Base URL"
            value={createDialog.baseUrl}
            onChange={(e) => setCreateDialog({ ...createDialog, baseUrl: e.target.value })}
            margin="normal"
            required
            placeholder="e.g., https://api.example.com"
            helperText="Base URL for external API integration (required)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={resetCreateDialog}>
            Cancel
          </Button>
          <Button onClick={handleCreateSubmit} variant="contained">
            Create API Key
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        fullScreen={isMobile}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              confirmDialog.onConfirm();
              setConfirmDialog({ ...confirmDialog, open: false });
            }}
            variant="contained"
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ApiKeys;
